/**
 * Footer Parallax Reveal Animation
 * 
 * Creates a scroll-based parallax effect where the footer slides down from above
 * the viewport when scrolling down, and slides back up when scrolling up.
 */
(function () {
    'use strict';

    class FooterParallax {
        constructor() {
            this.footer = null;
            this.wrapper = null;
            this.lastScrollY = 0;
            this.ticking = false;
            this.footerHeight = 0;
            this.isInitialized = false;
        }

        init() {
            // Get DOM elements
            this.footer = document.getElementById('main-footer');
            this.wrapper = document.getElementById('footer-parallax-wrapper');

            if (!this.footer || !this.wrapper) {
                console.warn('FooterParallax: Required elements not found');
                return;
            }

            // Calculate and set wrapper height to reserve space
            this.updateDimensions();

            // Set initial state - footer positioned above viewport
            this.footer.style.transform = 'translateY(-100%)';

            // Bind event handlers
            this.handleScroll = this.handleScroll.bind(this);
            this.handleResize = this.handleResize.bind(this);

            // Add event listeners
            window.addEventListener('scroll', this.handleScroll, { passive: true });
            window.addEventListener('resize', this.handleResize, { passive: true });

            // Do initial update
            this.isInitialized = true;
            this.updateParallax();
        }

        updateDimensions() {
            // Temporarily reset transform to get accurate height
            const currentTransform = this.footer.style.transform;
            this.footer.style.transform = 'none';

            this.footerHeight = this.footer.offsetHeight;
            this.wrapper.style.height = `${this.footerHeight}px`;

            // Restore transform
            this.footer.style.transform = currentTransform;
        }

        handleScroll() {
            if (!this.ticking) {
                requestAnimationFrame(() => {
                    this.updateParallax();
                    this.ticking = false;
                });
                this.ticking = true;
            }
        }

        handleResize() {
            this.updateDimensions();
            this.updateParallax();
        }

        updateParallax() {
            if (!this.isInitialized) return;

            const wrapperRect = this.wrapper.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const scrollY = window.scrollY;
            const documentHeight = document.documentElement.scrollHeight;

            // Check if we're at the bottom of the page
            const isAtBottom = (scrollY + viewportHeight) >= documentHeight - 5;

            if (isAtBottom) {
                // Fully reveal footer when at page bottom
                this.footer.style.transform = 'translateY(0%)';
                return;
            }

            // Calculate how far into the viewport the wrapper has scrolled
            // Start revealing when wrapper top is within 1.5x viewport height from bottom
            const revealStartPoint = viewportHeight * 1.5;

            if (wrapperRect.top > revealStartPoint) {
                // Footer not in range yet - keep hidden
                this.footer.style.transform = 'translateY(-100%)';
                return;
            }

            // Calculate progress based on how much of the wrapper is visible
            // Progress goes from 0 (just entering) to 1 (fully in view)
            let progress = 1 - (wrapperRect.top / revealStartPoint);

            // Clamp progress between 0 and 1
            progress = Math.max(0, Math.min(1, progress));

            // Calculate translateY value
            // At progress 0: translateY(-100%) - footer is above viewport (hidden)
            // At progress 1: translateY(0%) - footer is fully revealed
            const translateY = -100 + (progress * 100);

            // Apply transform
            this.footer.style.transform = `translateY(${translateY}%)`;

            // Store scroll position for direction detection (if needed in future)
            this.lastScrollY = scrollY;
        }

        destroy() {
            window.removeEventListener('scroll', this.handleScroll);
            window.removeEventListener('resize', this.handleResize);

            if (this.footer) {
                this.footer.style.transform = '';
            }
            if (this.wrapper) {
                this.wrapper.style.height = '';
            }

            this.isInitialized = false;
        }
    }

    // Initialize when DOM is ready
    function initFooterParallax() {
        const parallax = new FooterParallax();
        parallax.init();

        // Expose for debugging if needed
        window.footerParallax = parallax;
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initFooterParallax);
    } else {
        initFooterParallax();
    }
})();
